import {
  Button,
  Flex,
  Grid,
  GridCell,
  IconFileText,
  Tag,
  Text
} from '@vtex/shoreline'

import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import {
  formatInsertionOrderDate,
  formatInsertionOrderValue,
  isDocumentStatusPending,
  isSafeHttpUrl
} from './helpers'

import styles from './styles.module.css'

/** TEMP: keep false until download URLs are reliable. Flip to true to re-enable Open PDF. */
const ENABLE_OPEN_PDF = false

type PreviewProps = {
  insertionOrder: InsertionOrder
  onChange: () => void
  disabled?: boolean
}

export const LinkInsertionOrderPreview = ({
  insertionOrder,
  onChange,
  disabled
}: PreviewProps) => {
  const { t } = useAPPTranslation()
  const downloadUrl = insertionOrder?.file?.download_url
  const canOpenPdf = ENABLE_OPEN_PDF && isSafeHttpUrl(downloadUrl)
  const showPendingBadge = isDocumentStatusPending(
    insertionOrder?.document_status
  )

  const handleOpenPdf = () => {
    if (!canOpenPdf || !downloadUrl) return
    window.open(downloadUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className={styles.preview} data-testid="link-io-preview">
      <Flex
        className={styles.previewHead}
        justify="space-between"
        align="center"
        gap="$space-3"
      >
        <Flex align="center" gap="$space-2" style={{ minWidth: 0 }}>
          <Text as="div" variant="emphasis" data-testid="link-io-preview-name">
            {insertionOrder?.name}
          </Text>
          {showPendingBadge ? (
            <Tag
              color="yellow"
              variant="secondary"
              data-testid="link-io-document-status-pending"
            >
              {t('rm:campaign.insertionOrder.documentStatus.pending')}
            </Tag>
          ) : null}
        </Flex>
        <Flex align="center" gap="$space-4" style={{ flexShrink: 0 }}>
          {canOpenPdf ? (
            <Button
              type="button"
              variant="secondary"
              size="normal"
              onClick={handleOpenPdf}
              disabled={disabled}
              data-testid="link-io-open-pdf"
            >
              <IconFileText />
              {t('rm:campaign.insertionOrder.openPdf')}
            </Button>
          ) : null}
          <button
            type="button"
            className={styles.change}
            onClick={onChange}
            disabled={disabled}
            data-testid="link-io-change"
          >
            {t('rm:campaign.insertionOrder.change')}
          </button>
        </Flex>
      </Flex>

      <Grid
        className={styles.previewGrid}
        columns="repeat(4, minmax(0, 1fr))"
        gap="$space-4"
      >
        <GridCell>
          <Text as="div" variant="caption1" color="var(--sl-fg-base-soft)">
            {t('rm:campaign.insertionOrder.preview.id')}
          </Text>
          <Text
            as="div"
            variant="emphasis"
            className={styles.previewValue}
            data-testid="link-io-preview-id"
          >
            {insertionOrder?.sequential_id
              ? insertionOrder?.sequential_id
              : '-'}
          </Text>
        </GridCell>
        <GridCell>
          <Text as="div" variant="caption1" color="var(--sl-fg-base-soft)">
            {t('rm:campaign.insertionOrder.preview.value')}
          </Text>
          <Text
            as="div"
            variant="emphasis"
            className={styles.previewValue}
            data-testid="link-io-preview-value"
          >
            {formatInsertionOrderValue({
              totalValue: insertionOrder?.total_value,
              currency: insertionOrder?.currency
            })}
          </Text>
        </GridCell>
        <GridCell>
          <Text as="div" variant="caption1" color="var(--sl-fg-base-soft)">
            {t('rm:campaign.insertionOrder.preview.startDate')}
          </Text>
          <Text
            as="div"
            variant="emphasis"
            className={styles.previewValue}
            data-testid="link-io-preview-start"
          >
            {formatInsertionOrderDate(insertionOrder?.start_at)}
          </Text>
        </GridCell>
        <GridCell>
          <Text as="div" variant="caption1" color="var(--sl-fg-base-soft)">
            {t('rm:campaign.insertionOrder.preview.endDate')}
          </Text>
          <Text
            as="div"
            variant="emphasis"
            className={styles.previewValue}
            data-testid="link-io-preview-end"
          >
            {formatInsertionOrderDate(insertionOrder?.end_at)}
          </Text>
        </GridCell>
      </Grid>
    </div>
  )
}
